import { produce } from 'immer';
import { Layout } from 'react-grid-layout';
import { StateCreator } from 'zustand';
import { findNextAvailablePosition, getUniquePanelKey } from '@/dashboards/utils/panelUtils';
import { getPanelKeyFromRef, LayoutDefinition, PanelDefinition, SplitPanelDefinition } from '../../../core';
import { generateId, Middleware } from './common';

/**
 * Slice with the state of Panel Groups, as well as any actions that modify only Panel Group state.
 */
export interface PanelGroupSlice {
  /**
   * Panel groups indexed by their ID.
   */
  panelGroups: Record<PanelGroupId, PanelGroupDefinition>;

  /**
   * An array of panel group IDs, representing their order in the dashboard.
   */
  panelGroupOrder: PanelGroupId[];

  /**
   * Rearrange the order of panel groups by swapping the positions
   */
  swapPanelGroups: (xIndex: number, yIndex: number) => void;

  /**
   * Update the item layouts for a panel group when, for example, a panel is moved or resized.
   */
  updatePanelGroupLayouts: (panelGroupId: PanelGroupId, itemLayouts: PanelGroupGridDefinition['itemLayouts']) => void;

  /**
   * Update the panel group with the panelKey to be moved.
   */
  movePanelToPanelGroup: (
    currentPanelGroupId: PanelGroupId,
    currentPanelGroupLayoutId: string | undefined,
    panelKey: string,
    newPanelGroupId: number
  ) => void;

  /**
   * Duplicate a panel group with all its panels and layout.
   */
  duplicatePanelGroup: (panelGroupId: PanelGroupId) => void;
}

export type PanelGroupId = number;

export type PanelGroupDefinition = PanelGroupGridDefinition | PanelGroupSplitPanelDefinition;

export interface PanelGroup {
  id: PanelGroupId;
  kind: string;
  isCollapsed: boolean;
  title?: string;
}

export interface PanelGroupGridDefinition extends PanelGroup {
  kind: 'Grid';
  itemLayouts: PanelGroupItemLayout[];
  itemPanelKeys: Record<PanelGroupItemLayoutId, string>;
  splitEnabled?: boolean;
  splitVariableName?: string;
}

export interface PanelGroupSplitPanelDefinition extends PanelGroup {
  kind: 'SplitPanel';
  splitVariableName?: string;
  content: SplitPanelDefinition['spec']['content'];
}

export interface PanelGroupItemLayout extends Layout {
  i: PanelGroupItemLayoutId;
}

export type PanelGroupItemLayoutId = string;

/**
 * Uniquely identifies an item in a PanelGroup.
 */
export interface PanelGroupItemId {
  panelGroupId: PanelGroupId;
  panelGroupItemLayoutId?: PanelGroupItemLayoutId;
}

/**
 * Curried function for creating a PanelGroupSlice.
 */
export function createPanelGroupSlice(
  layouts: LayoutDefinition[]
): StateCreator<PanelGroupSlice, Middleware, [], PanelGroupSlice> {
  const { panelGroups, panelGroupOrder } = convertLayoutsToPanelGroups(layouts);

  // Return the state creator function for Zustand
  return (set) => ({
    panelGroups,
    panelGroupOrder,

    swapPanelGroups(x, y) {
      set(
        (state) => {
          if (x < 0 || x >= state.panelGroupOrder.length || y < 0 || y >= state.panelGroupOrder.length) {
            throw new Error('index out of bound');
          }
          const xPanelGroup = state.panelGroupOrder[x];
          const yPanelGroup = state.panelGroupOrder[y];

          if (xPanelGroup === undefined || yPanelGroup === undefined) {
            throw new Error('panel group is undefined');
          }
          // assign yPanelGroup to layouts[x] and assign xGroup to layouts[y], swapping two panel groups
          [state.panelGroupOrder[x], state.panelGroupOrder[y]] = [yPanelGroup, xPanelGroup];
        },
        false,
        '[Panel Groups] swapPanelGroups'
      );
    },

    updatePanelGroupLayouts(panelGroupId, itemLayouts) {
      set(
        (state) => {
          const group = state.panelGroups[panelGroupId];
          if (group === undefined) {
            throw new Error(`Cannot find panel group ${panelGroupId}`);
          } else if (group.kind !== 'Grid') {
            throw new Error(`Panel group ${panelGroupId} is not a Grid panel group`);
          }
          group.itemLayouts = itemLayouts;
        },
        false,
        '[Panel Groups] updatePanelGroupLayouts'
      );
    },

    movePanelToPanelGroup(currentPanelGroupId, currentPanelGroupLayoutId, panelKey, newPanelGroupId) {
      set(
        (state) => {
          const existingGroup = state.panelGroups[currentPanelGroupId];

          if (existingGroup === undefined) {
            throw new Error(`Invalid panel group ${currentPanelGroupId}`);
          }

          if (existingGroup.kind !== 'Grid') {
            throw new Error('Panel group was not kind "grid"');
          }

          if (currentPanelGroupLayoutId === undefined) {
            throw new Error('Invalid panel group layout id');
          }

          // Find the index of the layout to be moved
          const existingLayoutIdx = existingGroup.itemLayouts.findIndex(
            (layout) => layout.i === currentPanelGroupLayoutId
          );
          if (existingLayoutIdx === -1) {
            throw new Error(`Missing panel group item ${currentPanelGroupLayoutId}`);
          }

          const existingLayout = existingGroup.itemLayouts[existingLayoutIdx];
          const existingPanelKey = existingGroup.itemPanelKeys[currentPanelGroupLayoutId];
          if (!existingLayout || !existingPanelKey) {
            throw new Error(`Missing layout or panel key for panel group item ${currentPanelGroupLayoutId}`);
          }

          // Remove the item from the old group
          const updatedItemLayouts = existingGroup.itemLayouts;
          updatedItemLayouts.splice(existingLayoutIdx, 1);
          const updatedItemPanelKeys = produce(existingGroup.itemPanelKeys, (draft) => {
            delete draft[currentPanelGroupLayoutId];
          });

          // Assign the cloned and modified arrays back to the state
          existingGroup.itemLayouts = updatedItemLayouts;
          existingGroup.itemPanelKeys = updatedItemPanelKeys;

          // Add the item to the new group
          const newGroup = state.panelGroups[newPanelGroupId];
          if (newGroup === undefined || newGroup.kind !== 'Grid') {
            throw new Error(`Could not find new group ${newPanelGroupId}`);
          }

          const { x, y } = findNextAvailablePosition(newGroup.itemLayouts, existingLayout.w, existingLayout.h);
          const newLayout = produce(existingLayout, (draft) => {
            draft.x = x;
            draft.y = y;
          });
          newGroup.itemLayouts.push(newLayout);
          newGroup.itemPanelKeys[existingLayout.i] = existingPanelKey;
        },
        false,
        '[Panel Groups] movePanelToExistingGroup'
      );
    },

    duplicatePanelGroup(panelGroupId) {
      set(
        (state) => {
          const originalGroup = state.panelGroups[panelGroupId];
          if (originalGroup === undefined) {
            throw new Error(`Panel group ${panelGroupId} does not exist`);
          }

          // Create a new panel group with a unique ID
          const newGroupId = generateId();
          const newGroup: PanelGroupDefinition = produce(originalGroup, (draft) => {
            draft.id = newGroupId;
            draft.title = originalGroup.title ? `${originalGroup.title} (copy)` : 'Panel Group (copy)';
          });

          // Type assertion to access panels from the combined state
          const stateWithPanels = state as PanelGroupSlice & { panels: Record<string, PanelDefinition> };

          // Handle Grid panel groups
          if (originalGroup.kind === 'Grid') {
            const newGridGroup = newGroup as PanelGroupGridDefinition;
            newGridGroup.itemLayouts = [];
            newGridGroup.itemPanelKeys = {};

            // Duplicate each panel and its layout
            for (const layout of originalGroup.itemLayouts) {
              const originalPanelKey = originalGroup.itemPanelKeys[layout.i];
              if (originalPanelKey) {
                // Get the original panel definition from the store
                const originalPanel = stateWithPanels.panels[originalPanelKey];
                if (originalPanel) {
                  // Create a unique key for the duplicated panel
                  const newPanelKey = getUniquePanelKey({ panels: stateWithPanels.panels });

                  // Duplicate the panel definition
                  stateWithPanels.panels[newPanelKey] = produce(originalPanel, () => {});

                  // Create new layout with unique ID
                  const newLayoutId = generateId().toString();
                  newGridGroup.itemLayouts.push(
                    produce(layout, (draft) => {
                      draft.i = newLayoutId;
                    })
                  );
                  newGridGroup.itemPanelKeys[newLayoutId] = newPanelKey;
                }
              }
            }
          }
          // Handle SplitPanel groups
          else if (originalGroup.kind === 'SplitPanel') {
            const newSplitGroup = newGroup as PanelGroupSplitPanelDefinition;
            const originalPanelKey = getPanelKeyFromRef(originalGroup.content);

            if (originalPanelKey) {
              // Get the original panel definition
              const originalPanel = stateWithPanels.panels[originalPanelKey];
              if (originalPanel) {
                // Create a unique key for the duplicated panel
                const newPanelKey = getUniquePanelKey({ panels: stateWithPanels.panels });

                // Duplicate the panel definition
                stateWithPanels.panels[newPanelKey] = produce(originalPanel, () => {});

                // Update the content reference
                newSplitGroup.content = {
                  $ref: `#/spec/panels/${newPanelKey}`,
                };
              }
            }
          }

          // Add the new group to the state
          state.panelGroups[newGroupId] = newGroup;

          // Insert the new group right after the original group in the order
          const originalIndex = state.panelGroupOrder.indexOf(panelGroupId);
          if (originalIndex !== -1) {
            state.panelGroupOrder.splice(originalIndex + 1, 0, newGroupId);
          } else {
            // If original group not found in order, add to the beginning
            state.panelGroupOrder.unshift(newGroupId);
          }
        },
        false,
        '[Panel Groups] duplicatePanelGroup'
      );
    },
  });
}

export function convertLayoutsToPanelGroups(
  layouts: LayoutDefinition[]
): Pick<PanelGroupSlice, 'panelGroups' | 'panelGroupOrder'> {
  // Convert the initial layouts from the JSON
  const panelGroups: PanelGroupSlice['panelGroups'] = {};
  const panelGroupIdOrder: PanelGroupSlice['panelGroupOrder'] = [];

  for (const layout of layouts) {
    // Create the panel group and keep track of the ID order
    const panelGroupId = generateId();

    const panelGroupCommon = {
      kind: layout.kind,
      id: panelGroupId,
      // Default to not collapsed if not specified
      isCollapsed: layout.spec.display?.collapse?.open === false,
      title: layout.spec.display?.title,
    };

    let panelGroup: PanelGroupDefinition | undefined = undefined;
    if (layout.kind === 'Grid') {
      const itemLayouts: PanelGroupGridDefinition['itemLayouts'] = [];
      const itemPanelKeys: PanelGroupGridDefinition['itemPanelKeys'] = {};
      // Split layout information from panel keys to make it easier to update just layouts on move/resize of panels
      for (const item of layout.spec.items) {
        const panelGroupLayoutId = generateId().toString();
        itemLayouts.push({
          i: panelGroupLayoutId,
          w: item.width,
          h: item.height,
          x: item.x,
          y: item.y,
        });
        itemPanelKeys[panelGroupLayoutId] = getPanelKeyFromRef(item.content);
      }
      panelGroup = {
        ...panelGroupCommon,
        kind: 'Grid',
        itemLayouts,
        itemPanelKeys,
        splitEnabled: !!layout.spec.split_by?.variable,
        splitVariableName: layout.spec.split_by?.variable,
      };
    } else if (layout.kind === 'SplitPanel') {
      panelGroup = {
        ...panelGroupCommon,
        kind: 'SplitPanel',
        splitVariableName: layout.spec.split_by?.variable,
        content: layout.spec.content,
      };
    }

    // Only add the panel group if it was successfully created
    if (panelGroup !== undefined) {
      panelGroups[panelGroupId] = panelGroup;
      panelGroupIdOrder.push(panelGroupId);
    }
  }
  return {
    panelGroups,
    panelGroupOrder: panelGroupIdOrder,
  };
}

/**
 * Private helper function for creating an empty panel group.
 */
export function createEmptyPanelGroup(): PanelGroupDefinition {
  return {
    kind: 'Grid',
    id: generateId(),
    title: undefined,
    isCollapsed: false,
    itemLayouts: [],
    itemPanelKeys: {},
    splitEnabled: false,
    splitVariableName: undefined,
  };
}

/**
 * Private helper function that modifies panel group state to add a new panel
 */
export function addPanelGroup(draft: PanelGroupSlice, newGroup: PanelGroupDefinition) {
  draft.panelGroups[newGroup.id] = newGroup;
  draft.panelGroupOrder.unshift(newGroup.id);
}
